<template>
  <component :is="elementTag" v-bind="$attrs" :class="['text', textClass, colorClass, uppercaseClass]">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

defineOptions({
  inheritAttrs: false,
});

type Variant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body' | 'caption' | 'section' | 'label';
type Size = 'lg' | 'md' | 'sm' | 'xs';
type Weight = 'regular' | 'medium';
type Color = 'default' | 'error' | 'muted' | 'secondary' | 'tertiary' | 'inherit';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    size?: Size;
    weight?: Weight;
    color?: Color;
    as?: string;
    uppercase?: boolean;
  }>(),
  {
    variant: 'body',
    size: 'md',
    weight: 'regular',
    color: 'default',
    as: undefined,
  },
);

const elementTag = computed(() => {
  if (props.as) {
    return props.as;
  }

  const variant = props.variant;
  if (variant.startsWith('h') && /^h[1-6]$/.test(variant)) {
    return variant;
  }
  if (variant === 'body' || variant === 'caption' || variant === 'section') {
    return 'p';
  }
  if (variant === 'label') {
    return 'label';
  }
  return 'p';
});

const textClass = computed(() => {
  const variant = props.variant;
  const size = props.size;
  const weight = props.weight;

  if (variant.startsWith('h') && /^h[1-6]$/.test(variant)) {
    const headerMap = {
      h1: 'header-xxl',
      h2: 'header-xl',
      h3: 'header-lg',
      h4: 'header-md',
      h5: 'header-sm',
      h6: 'header-xs',
    };
    return headerMap[variant as keyof typeof headerMap] || '';
  }

  if (variant === 'body') {
    return `body-${size}-${weight}`;
  }

  if (variant === 'caption') {
    return `caption-${weight}`;
  }

  if (variant === 'section') {
    const weightSuffix = weight === 'medium' ? 'bold' : '';
    return weightSuffix ? `section-${size}-${weightSuffix}` : `section-${size}`;
  }

  if (variant === 'label') {
    return `label-${size}`;
  }

  return '';
});

const colorClass = computed(() => {
  if (!props.color || props.color === 'inherit') return '';
  return `text-${props.color}`;
});

const uppercaseClass = computed(() => {
  return props.uppercase ? 'text-uppercase' : '';
});
</script>

<style scoped>
.text {
  outline: none;
  /* Own the box: body variants render as <p> and would inherit the global paragraph margin. */
  margin: 0;
}

.text-default {
  color: var(--p-grey-950);
}

.text-error {
  color: var(--p-red-500);
}

.text-muted {
  color: var(--grey-40);
}

.text-secondary {
  color: var(--p-text-secondary);
}

.text-tertiary {
  color: var(--p-surface-500);
}

.text-uppercase {
  text-transform: uppercase;
}

.header-xxl {
  font-size: var(--p-typeset-header-xxl-font-size, 40px);
  line-height: var(--p-typeset-header-xxl-line-height, 110%);
  letter-spacing: var(--p-typeset-header-xxl-letter-spacing, 0.8px);
  font-weight: var(--p-typeset-header-xxl-font-weight, 800);
  font-family: var(--p-typeset-header-xxl-font-family, inherit);
  margin: 0;
}

.header-xl {
  font-size: var(--p-typeset-header-xl-font-size, 32px);
  line-height: var(--p-typeset-header-xl-line-height, 110%);
  letter-spacing: var(--p-typeset-header-xl-letter-spacing, 0.8px);
  font-weight: var(--p-typeset-header-xl-font-weight, 800);
  font-family: var(--p-typeset-header-xl-font-family, inherit);
  margin: 0;
}

.header-lg {
  font-size: var(--p-typeset-header-lg-font-size, 24px);
  line-height: var(--p-typeset-header-lg-line-height, 110%);
  letter-spacing: var(--p-typeset-header-lg-letter-spacing, 0.8px);
  font-weight: var(--p-typeset-header-lg-font-weight, 800);
  font-family: var(--p-typeset-header-lg-font-family, inherit);
  margin: 0;
}

.header-md {
  font-size: var(--p-typeset-header-md-font-size, 20px);
  line-height: var(--p-typeset-header-md-line-height, 120%);
  letter-spacing: var(--p-typeset-header-md-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-header-md-font-weight, 700);
  font-family: var(--p-typeset-header-md-font-family, inherit);
  margin: 0;
}

.header-sm {
  font-size: var(--p-typeset-header-sm-font-size, 16px);
  line-height: var(--p-typeset-header-sm-line-height, 120%);
  letter-spacing: var(--p-typeset-header-sm-letter-spacing, 0.8px);
  font-weight: var(--p-typeset-header-sm-font-weight, 700);
  font-family: var(--p-typeset-header-sm-font-family, inherit);
  margin: 0;
}

.header-xs {
  font-size: var(--p-typeset-header-xs-font-size, 14px);
  line-height: var(--p-typeset-header-xs-line-height, 125%);
  letter-spacing: var(--p-typeset-header-xs-letter-spacing, 1px);
  font-weight: var(--p-typeset-header-xs-font-weight, 800);
  font-family: var(--p-typeset-header-xs-font-family, inherit);
  margin: 0;
}

.body-lg-regular {
  font-size: var(--p-typeset-body-lg-regular-font-size, 20px);
  line-height: var(--p-typeset-body-lg-regular-line-height, 130%);
  letter-spacing: var(--p-typeset-body-lg-regular-letter-spacing, 0.3px);
  font-weight: var(--p-typeset-body-lg-regular-font-weight, 400);
  font-family: var(--p-typeset-body-lg-regular-font-family, inherit);
}

.body-md-regular {
  font-size: var(--p-typeset-body-md-regular-font-size, 16px);
  line-height: var(--p-typeset-body-md-regular-line-height, 140%);
  letter-spacing: var(--p-typeset-body-md-regular-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-body-md-regular-font-weight, 400);
  font-family: var(--p-typeset-body-md-regular-font-family, inherit);
}

.body-sm-regular {
  font-size: var(--p-typeset-body-sm-regular-font-size, 14px);
  line-height: var(--p-typeset-body-sm-regular-line-height, 140%);
  letter-spacing: var(--p-typeset-body-sm-regular-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-body-sm-regular-font-weight, 400);
  font-family: var(--p-typeset-body-sm-regular-font-family, inherit);
}

.body-lg-medium {
  font-size: var(--p-typeset-body-lg-medium-font-size, 20px);
  line-height: var(--p-typeset-body-lg-medium-line-height, 130%);
  letter-spacing: var(--p-typeset-body-lg-medium-letter-spacing, 0.3px);
  font-weight: var(--p-typeset-body-lg-medium-font-weight, 600);
  font-family: var(--p-typeset-body-lg-medium-font-family, inherit);
}

.body-md-medium {
  font-size: var(--p-typeset-body-md-medium-font-size, 16px);
  line-height: var(--p-typeset-body-md-medium-line-height, 140%);
  letter-spacing: var(--p-typeset-body-md-medium-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-body-md-medium-font-weight, 600);
  font-family: var(--p-typeset-body-md-medium-font-family, inherit);
}

.body-sm-medium {
  font-size: var(--p-typeset-body-sm-medium-font-size, 14px);
  line-height: var(--p-typeset-body-sm-medium-line-height, 140%);
  letter-spacing: var(--p-typeset-body-sm-medium-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-body-sm-medium-font-weight, 600);
  font-family: var(--p-typeset-body-sm-medium-font-family, inherit);
}

.caption-regular,
.body-xs-regular {
  font-size: var(--p-typeset-caption-regular-font-size, 12px);
  line-height: var(--p-typeset-caption-regular-line-height, 140%);
  letter-spacing: var(--p-typeset-caption-regular-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-caption-regular-font-weight, 400);
  font-family: var(--p-typeset-caption-regular-font-family, inherit);
}

.caption-medium,
.body-xs-medium {
  font-size: var(--p-typeset-caption-medium-font-size, 12px);
  line-height: var(--p-typeset-caption-medium-line-height, 140%);
  letter-spacing: var(--p-typeset-caption-medium-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-caption-medium-font-weight, 600);
  font-family: var(--p-typeset-caption-medium-font-family, inherit);
}

.section-lg {
  font-size: var(--p-typeset-section-lg-font-size, 16px);
  line-height: var(--p-typeset-section-lg-line-height, 115%);
  letter-spacing: var(--p-typeset-section-lg-letter-spacing, 1.4px);
  font-weight: var(--p-typeset-section-lg-font-weight, 700);
  font-family: var(--p-typeset-section-lg-font-family, inherit);
}

.section-md {
  font-size: var(--p-typeset-section-md-font-size, 14px);
  line-height: var(--p-typeset-section-md-line-height, 120%);
  letter-spacing: var(--p-typeset-section-md-letter-spacing, 1.2px);
  font-weight: var(--p-typeset-section-md-font-weight, 700);
  font-family: var(--p-typeset-section-md-font-family, inherit);
}

.section-sm,
.section-xs {
  font-size: var(--p-typeset-section-sm-font-size, 12px);
  line-height: var(--p-typeset-section-sm-line-height, 120%);
  letter-spacing: var(--p-typeset-section-sm-letter-spacing, 1.2px);
  font-weight: var(--p-typeset-section-sm-font-weight, 500);
  font-family: var(--p-typeset-section-sm-font-family, inherit);
}

.section-lg-bold {
  font-size: var(--p-typeset-section-lg-bold-font-size, 16px);
  line-height: var(--p-typeset-section-lg-bold-line-height, 115%);
  letter-spacing: var(--p-typeset-section-lg-bold-letter-spacing, 1.4px);
  font-weight: var(--p-typeset-section-lg-bold-font-weight, 900);
  font-family: var(--p-typeset-section-lg-bold-font-family, inherit);
}

.section-md-bold {
  font-size: var(--p-typeset-section-md-bold-font-size, 14px);
  line-height: var(--p-typeset-section-md-bold-line-height, 120%);
  letter-spacing: var(--p-typeset-section-md-bold-letter-spacing, 1.2px);
  font-weight: var(--p-typeset-section-md-bold-font-weight, 900);
  font-family: var(--p-typeset-section-md-bold-font-family, inherit);
}

.section-sm-bold,
.section-xs-bold {
  font-size: var(--p-typeset-section-sm-bold-font-size, 12px);
  line-height: var(--p-typeset-section-sm-bold-line-height, 120%);
  letter-spacing: var(--p-typeset-section-sm-bold-letter-spacing, 1.2px);
  font-weight: var(--p-typeset-section-sm-bold-font-weight, 900);
  font-family: var(--p-typeset-section-sm-bold-font-family, inherit);
}

.label-lg {
  font-size: var(--p-typeset-body-lg-medium-font-size, 20px);
  line-height: var(--p-typeset-body-lg-medium-line-height, 130%);
  letter-spacing: var(--p-typeset-body-lg-medium-letter-spacing, 0.3px);
  font-weight: var(--p-typeset-body-lg-medium-font-weight, 600);
  font-family: var(--p-typeset-body-lg-medium-font-family, inherit);
}

.label-md {
  font-size: var(--p-typeset-body-md-medium-font-size, 16px);
  line-height: var(--p-typeset-body-md-medium-line-height, 140%);
  letter-spacing: var(--p-typeset-body-md-medium-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-body-md-medium-font-weight, 600);
  font-family: var(--p-typeset-body-md-medium-font-family, inherit);
}

.label-sm,
.label-xs {
  font-size: var(--p-typeset-body-sm-medium-font-size, 14px);
  line-height: var(--p-typeset-body-sm-medium-line-height, 140%);
  letter-spacing: var(--p-typeset-body-sm-medium-letter-spacing, 0.5px);
  font-weight: var(--p-typeset-body-sm-medium-font-weight, 600);
  font-family: var(--p-typeset-body-sm-medium-font-family, inherit);
}
</style>
